import React from 'react';
import { defaultMemoize } from 'reselect';
import { Messages } from '../../../../../gen-src/Localize';
import { FlowQuery } from '../../../../../gen-src/queries';
import {
  DropdownWrapper,
  messageWithOrWithoutCount,
  SelectionProviderChildProps
} from '../../../data-catalog-shared';
import { FlowCustomSqlQueryWrapper } from '../CustomSql/FlowCustomSqlQueryWrapper';
import { LineageItemType } from '../lineage/LineageItemType';
import { LineageItemConfig, LineageSection, LineageSectionType } from '../placePage/LineageConfiguration';
import { LineageSubplaceDropdownLabel } from '../placePage/LineageSubplaceDropdownLabel';
import { PaneWithTitle } from '../placePage/PaneWithTitle';
import { DashboardsGridSortProvider } from '../sorting/sortProviders/DashboardsGridSortProvider';
import { DatabasesGridSortProvider } from '../sorting/sortProviders/DatabasesGridSortProvider';
import { DatasourcesGridSortProvider } from '../sorting/sortProviders/DatasourcesGridSortProvider';
import { FieldsGridSortProvider } from '../sorting/sortProviders/FieldsGridSortProvider';
import { FlowsGridSortProvider } from '../sorting/sortProviders/FlowsGridSortProvider';
import { LensesGridSortProvider } from '../sorting/sortProviders/LensesGridSortProvider';
import { MetricsGridSortProvider } from '../sorting/sortProviders/MetricsGridSortProvider';
import { SheetsGridSortProvider } from '../sorting/sortProviders/SheetsGridSortProvider';
import { TablesGridSortProvider } from '../sorting/sortProviders/TablesGridSortProvider';
import { VirtualConnectionTablesGridSortProvider } from '../sorting/sortProviders/VirtualConnectionTablesGridSortProvider';
import { VirtualConnectionsGridSortProvider } from '../sorting/sortProviders/VirtualConnectionsGridSortProvider';
import { WorkbooksGridSortProvider } from '../sorting/sortProviders/WorkbooksGridSortProvider';
import { FlowDownstreamDashboardsQueryGrid } from './FlowDownstreamDashboardsQueryGrid';
import { FlowDownstreamDatabasesQueryGrid } from './FlowDownstreamDatabasesQueryGrid';
import { FlowDownstreamDatasourcesQueryGrid } from './FlowDownstreamDatasourcesQueryGrid';
import { FlowDownstreamFlowsQueryGrid } from './FlowDownstreamFlowsQueryGrid';
import { FlowDownstreamLensesQueryGrid } from './FlowDownstreamLensesQueryGrid';
import { FlowDownstreamMetricsQueryGrid } from './FlowDownstreamMetricsQueryGrid';
import { FlowDownstreamOwnersPane } from './FlowDownstreamOwnersPane';
import { FlowDownstreamSheetsQueryGrid } from './FlowDownstreamSheetsQueryGrid';
import { FlowDownstreamTablesQueryGrid } from './FlowDownstreamTablesQueryGrid';
import { FlowDownstreamWorkbooksQueryGrid } from './FlowDownstreamWorkbooksQueryGrid';
import { FlowFieldsQueryGrid } from './FlowFieldsQueryGrid';
import { FlowLineageCounts } from './FlowLineageCountsQueryWrapper';
import { FlowOutputStepSelector } from './FlowOutputStepSelector';
import { FlowParametersWarning } from './FlowParametersWarning';
import { FlowUpstreamDatabasesQueryGrid } from './FlowUpstreamDatabasesQueryGrid';
import { FlowUpstreamDatasourcesQueryGrid } from './FlowUpstreamDatasourcesQueryGrid';
import { FlowUpstreamFlowsQueryGrid } from './FlowUpstreamFlowsQueryGrid';
import { FlowUpstreamTablesQueryGrid } from './FlowUpstreamTablesQueryGrid';
import { FlowUpstreamVirtualConnectionTablesQueryGrid } from './FlowUpstreamVirtualConnectionTablesQueryGrid';
import { FlowUpstreamVirtualConnectionsQueryGrid } from './FlowUpstreamVirtualConnectionsQueryGrid';

export type FlowLineageItemConfig = LineageItemConfig<FlowLineageCounts, FlowLineagePaneParams>;
export type FlowLineageSection = LineageSection<FlowLineageCounts, FlowLineagePaneParams>;
type FlowQuery_flows_outputSteps = Unpacked<Unpacked<FlowQuery['flows']>['outputSteps']>

export type FlowLineagePaneParams = SelectionProviderChildProps & {
  readonly title: string;
  readonly flowVizportalUrlId: string;
  readonly filteredFlowOutputFieldId: string | null;
  readonly selectedOutputStepId: string;
  readonly fieldsCountForSelectedOutputStep: number | null;
  readonly count: number | null;
  readonly updateOutputStepSelection: (outputStepId: string) => void;
  readonly outputSteps: ReadonlyArray<FlowQuery_flows_outputSteps>;
  readonly shouldShowFlowParametersWarning?: boolean;
  readonly flowLuid: string;
};

const flowUpstreamVirtualConnection: FlowLineageItemConfig = {
  lineageItemType: LineageItemType.VirtualConnection,
  messagesForLineage: { withCount: Messages.VirtualConnectionsWithCount, withoutCount: Messages.VirtualConnections },
  messagesForPane: { withCount: Messages.UpstreamVirtualConnectionsWithCount, withoutCount: Messages.UpstreamVirtualConnections },
  count: counts => counts.upstreamVirtualConnections,
  path: (urls, vizportalUrlId) => urls.flowLineageUpstreamVirtualConnectionsPath(vizportalUrlId),
  paneFactory: (paneParams) => (
    <PaneWithTitle title={paneParams.title}>
      <VirtualConnectionsGridSortProvider>
        {({ orderBy, updateSortOrder }) =>
          <FlowUpstreamVirtualConnectionsQueryGrid
            flowVizportalUrlId={paneParams.flowVizportalUrlId}
            filteredFlowOutputFieldId={paneParams.filteredFlowOutputFieldId}
            rowCount={paneParams.count}
            orderBy={orderBy}
            updateSortOrder={updateSortOrder}
          />
        }
      </VirtualConnectionsGridSortProvider>
    </PaneWithTitle>
  ),
};
const flowUpstreamVirtualConnectionTable: FlowLineageItemConfig = {
  lineageItemType: LineageItemType.VirtualConnectionTable,
  messagesForLineage: { withCount: Messages.VirtualConnectionTablesWithCount, withoutCount: Messages.VirtualConnectionTables },
  messagesForPane: { withCount: Messages.UpstreamVirtualConnectionTablesWithCount, withoutCount: Messages.UpstreamVirtualConnectionTables },
  count: counts => counts.upstreamVirtualConnectionTables,
  path: (urls, vizportalUrlId) => urls.flowLineageUpstreamVirtualConnectionTablesPath(vizportalUrlId),
  paneFactory: (paneParams) => (
    <PaneWithTitle title={paneParams.title}>
      <VirtualConnectionTablesGridSortProvider>
        {({ orderBy, updateSortOrder }) =>
          <FlowUpstreamVirtualConnectionTablesQueryGrid
            flowVizportalUrlId={paneParams.flowVizportalUrlId}
            filteredFlowOutputFieldId={paneParams.filteredFlowOutputFieldId}
            rowCount={paneParams.count}
            orderBy={orderBy}
            updateSortOrder={updateSortOrder}
          />
        }
      </VirtualConnectionTablesGridSortProvider>
    </PaneWithTitle>
  ),
};
function getFlowUpstreamItems(publishedConnectionsLineageEnabled: boolean): ReadonlyArray<FlowLineageItemConfig> {
  return [
    {
      lineageItemType: LineageItemType.Database,
      messagesForLineage: { withCount: Messages.DatabasesWithCount, withoutCount: Messages.Databases },
      messagesForPane: { withCount: Messages.UpstreamDatabasesWithCount, withoutCount: Messages.UpstreamDatabases },
      count: counts => counts.upstreamDatabases,
      path: (urls, id) => urls.flowLineageUpstreamDatabasesPath(id),
      paneFactory: (paneParams) => (
        <PaneWithTitle title={paneParams.title}>
          <DatabasesGridSortProvider>
            {({ orderBy, updateSortOrder }) =>
              <FlowUpstreamDatabasesQueryGrid
                flowVizportalUrlId={paneParams.flowVizportalUrlId}
                filteredFlowOutputFieldId={paneParams.filteredFlowOutputFieldId}
                rowCount={paneParams.count}
                orderBy={orderBy}
                updateSortOrder={updateSortOrder}
              />
            }
          </DatabasesGridSortProvider>
        </PaneWithTitle>
      ),
    },
    {
      lineageItemType: LineageItemType.Table,
      messagesForLineage: { withCount: Messages.TablesWithCount, withoutCount: Messages.Tables },
      messagesForPane: { withCount: Messages.UpstreamTablesWithCount, withoutCount: Messages.UpstreamTables },
      count: counts => counts.upstreamTables,
      path: (urls, id) => urls.flowLineageUpstreamTablesPath(id),
      paneFactory: (paneParams) => (
        <PaneWithTitle title={paneParams.title}>
          <TablesGridSortProvider>
            {({ orderBy, updateSortOrder }) =>
              <FlowUpstreamTablesQueryGrid
                flowVizportalUrlId={paneParams.flowVizportalUrlId}
                filteredFlowOutputFieldId={paneParams.filteredFlowOutputFieldId}
                rowCount={paneParams.count}
                orderBy={orderBy}
                updateSortOrder={updateSortOrder}
              />
            }
          </TablesGridSortProvider>
        </PaneWithTitle>
      ),
    },
    ...(publishedConnectionsLineageEnabled ? [flowUpstreamVirtualConnection, flowUpstreamVirtualConnectionTable] : []),
    {
      lineageItemType: LineageItemType.Flow,
      messagesForLineage: { withCount: Messages.FlowsWithCount, withoutCount: Messages.Flows },
      messagesForPane: { withCount: Messages.UpstreamFlowsWithCount, withoutCount: Messages.UpstreamFlows },
      count: counts => counts.upstreamFlows,
      path: (urls, vizportalUrlId) => urls.flowLineageUpstreamFlowsPath(vizportalUrlId),
      paneFactory: (paneParams) => (
        <PaneWithTitle title={paneParams.title}>
          <FlowsGridSortProvider>
            {({ orderBy, updateSortOrder }) =>
              <FlowUpstreamFlowsQueryGrid
                flowVizportalUrlId={paneParams.flowVizportalUrlId}
                filteredFlowOutputFieldId={paneParams.filteredFlowOutputFieldId}
                rowCount={paneParams.count}
                orderBy={orderBy}
                updateSortOrder={updateSortOrder}
              />
            }
          </FlowsGridSortProvider>
        </PaneWithTitle>
      ),
    },
    {
      lineageItemType: LineageItemType.Datasource,
      messagesForLineage: { withCount: Messages.DatasourcesWithCount, withoutCount: Messages.Datasources },
      messagesForPane: { withCount: Messages.UpstreamDatasourcesWithCount, withoutCount: Messages.UpstreamDatasources },
      count: counts => counts.upstreamDatasources,
      path: (urls, vizportalUrlId) => urls.flowLineageUpstreamDatasourcesPath(vizportalUrlId),
      paneFactory: (paneParams) => (
        <PaneWithTitle title={paneParams.title}>
          <DatasourcesGridSortProvider>
            {({ orderBy, updateSortOrder }) =>
              <FlowUpstreamDatasourcesQueryGrid
                flowVizportalUrlId={paneParams.flowVizportalUrlId}
                filteredFlowOutputFieldId={paneParams.filteredFlowOutputFieldId}
                rowCount={paneParams.count}
                orderBy={orderBy}
                updateSortOrder={updateSortOrder}
              />}
          </DatasourcesGridSortProvider>
        </PaneWithTitle>
      ),
    },
  ];
}
const memoizedGetFlowUpstreamItems = defaultMemoize(getFlowUpstreamItems);

function FlowAnchor(): FlowLineageItemConfig {
  return {
    lineageItemType: LineageItemType.Flow,
    messagesForLineage: { withCount: Messages.OutputStepsWithCount, withoutCount: Messages.OutputSteps },
    messagesForPane: { withCount: Messages.FieldsWithCount, withoutCount: Messages.Fields },
    count: _ => NaN, // The anchor gets its own count from the LineageNav props, and the two pane counts are passed in via paneParams.
    path: (urls, id) => urls.flowLineageFieldsPath(id),
    paneFactory: (paneParams) => (
      <>
        {paneParams.shouldShowFlowParametersWarning &&
          <FlowParametersWarning />
        }
        <div>
          <LineageSubplaceDropdownLabel
            label={messageWithOrWithoutCount(paneParams.outputSteps.length, {
              withCount: Messages.OutputStepsWithCount,
              withoutCount: Messages.OutputSteps
            })}
          >
            <DropdownWrapper>
              <FlowOutputStepSelector
                outputSteps={paneParams.outputSteps}
                updateOutputStepSelection={paneParams.updateOutputStepSelection}
                selectedOutputStep={paneParams.selectedOutputStepId}
              />
            </DropdownWrapper>
          </LineageSubplaceDropdownLabel>
        </div>
        {
          <FlowCustomSqlQueryWrapper
            flowId={paneParams.flowVizportalUrlId}
            outputStepId={paneParams.selectedOutputStepId}
          />

        }
        <PaneWithTitle
          title={messageWithOrWithoutCount(
            paneParams.fieldsCountForSelectedOutputStep,
            { withCount: Messages.FieldsWithCount, withoutCount: Messages.Fields },
          )}
        >
          <FieldsGridSortProvider>
            {({ orderBy, updateSortOrder }) =>
              <FlowFieldsQueryGrid
                flowVizportalUrlId={paneParams.flowVizportalUrlId}
                selectedKeys={paneParams.selectedKeys}
                selectedOutputStepId={paneParams.selectedOutputStepId}
                updateSelectionForKey={paneParams.updateSelectionForKey}
                rowCount={paneParams.fieldsCountForSelectedOutputStep}
                orderBy={orderBy}
                updateSortOrder={updateSortOrder}
              />
            }
          </FieldsGridSortProvider>
        </PaneWithTitle>
      </>
    ),
  };
}

const flowDownstreamMetrics: FlowLineageItemConfig = {
  lineageItemType: LineageItemType.Metric,
  messagesForLineage: { withCount: Messages.MetricsWithCount, withoutCount: Messages.Metrics },
  messagesForPane: { withCount: Messages.DownstreamMetricsWithCount, withoutCount: Messages.DownstreamMetrics },
  count: counts => counts.downstreamMetrics,
  path: (urls, id) => urls.flowLineageDownstreamMetricsPath(id),
  paneFactory: (paneParams) => (
    <PaneWithTitle title={paneParams.title}>
      <MetricsGridSortProvider>
        {({ orderBy, updateSortOrder }) =>
          <FlowDownstreamMetricsQueryGrid
            flowVizportalUrlId={paneParams.flowVizportalUrlId}
            filteredFlowOutputFieldId={paneParams.filteredFlowOutputFieldId}
            rowCount={paneParams.count}
            orderBy={orderBy}
            updateSortOrder={updateSortOrder}
          />}
      </MetricsGridSortProvider>
    </PaneWithTitle>
  ),
};

const flowDownstreamLenses: FlowLineageItemConfig = {
  lineageItemType: LineageItemType.Lens,
  messagesForLineage: { withCount: Messages.LensesWithCount, withoutCount: Messages.Lenses },
  messagesForPane: { withCount: Messages.DownstreamLensesWithCount, withoutCount: Messages.DownstreamLenses },
  count: counts => counts.downstreamLenses,
  path: (urls, vizportalUrlId) => urls.flowLineageDownstreamLensesPath(vizportalUrlId),
  paneFactory: (paneParams) => (
    <PaneWithTitle title={paneParams.title}>
      <LensesGridSortProvider>
        {({ orderBy, updateSortOrder }) =>
          <FlowDownstreamLensesQueryGrid
            vizportalUrlId={paneParams.flowVizportalUrlId}
            rowCount={paneParams.count}
            orderBy={orderBy}
            updateSortOrder={updateSortOrder}
            filteredFlowOutputFieldId={paneParams.filteredFlowOutputFieldId}
          />}
      </LensesGridSortProvider>
    </PaneWithTitle>
  ),
};

function getFlowDownstreamItems(metricsDataCatalogEnabled: boolean, lensLineageEnabled: boolean): ReadonlyArray<FlowLineageItemConfig> {
  return [
    {
      lineageItemType: LineageItemType.Database,
      messagesForLineage: { withCount: Messages.DatabasesWithCount, withoutCount: Messages.Databases },
      messagesForPane: { withCount: Messages.DownstreamDatabasesWithCount, withoutCount: Messages.DownstreamDatabases },
      count: counts => counts.downstreamDatabases,
      path: (urls, id) => urls.flowLineageDownstreamDatabasesPath(id),
      paneFactory: (paneParams) => (
        <PaneWithTitle title={paneParams.title}>
          <DatabasesGridSortProvider>
            {({ orderBy, updateSortOrder }) =>
              <FlowDownstreamDatabasesQueryGrid
                flowVizportalUrlId={paneParams.flowVizportalUrlId}
                filteredFlowOutputFieldId={paneParams.filteredFlowOutputFieldId}
                rowCount={paneParams.count}
                orderBy={orderBy}
                updateSortOrder={updateSortOrder}
              />
            }
          </DatabasesGridSortProvider>
        </PaneWithTitle>
      ),
    },
    {
      lineageItemType: LineageItemType.Table,
      messagesForLineage: { withCount: Messages.TablesWithCount, withoutCount: Messages.Tables },
      messagesForPane: { withCount: Messages.DownstreamTablesWithCount, withoutCount: Messages.DownstreamTables },
      count: counts => counts.downstreamTables,
      path: (urls, id) => urls.flowLineageDownstreamTablesPath(id),
      paneFactory: (paneParams) => (
        <PaneWithTitle title={paneParams.title}>
          <TablesGridSortProvider>
            {({ orderBy, updateSortOrder }) =>
              <FlowDownstreamTablesQueryGrid
                flowVizportalUrlId={paneParams.flowVizportalUrlId}
                filteredFlowOutputFieldId={paneParams.filteredFlowOutputFieldId}
                rowCount={paneParams.count}
                orderBy={orderBy}
                updateSortOrder={updateSortOrder}
              />
            }
          </TablesGridSortProvider>
        </PaneWithTitle >
      ),
    },
    {
      lineageItemType: LineageItemType.Flow,
      messagesForLineage: { withCount: Messages.FlowsWithCount, withoutCount: Messages.Flows },
      messagesForPane: { withCount: Messages.DownstreamFlowsWithCount, withoutCount: Messages.DownstreamFlows },
      count: counts => counts.downstreamFlows,
      path: (urls, vizportalUrlId) => urls.flowLineageDownstreamFlowsPath(vizportalUrlId),
      paneFactory: (paneParams) => (
        <PaneWithTitle title={paneParams.title}>
          <FlowsGridSortProvider>
            {({ orderBy, updateSortOrder }) =>
              <FlowDownstreamFlowsQueryGrid
                flowVizportalUrlId={paneParams.flowVizportalUrlId}
                filteredFlowOutputFieldId={paneParams.filteredFlowOutputFieldId}
                rowCount={paneParams.count}
                orderBy={orderBy}
                updateSortOrder={updateSortOrder}
              />
            }
          </FlowsGridSortProvider>
        </PaneWithTitle>
      ),
    },
    {
      lineageItemType: LineageItemType.Datasource,
      messagesForLineage: { withCount: Messages.DatasourcesWithCount, withoutCount: Messages.Datasources },
      messagesForPane: { withCount: Messages.DownstreamDatasourcesWithCount, withoutCount: Messages.DownstreamDatasources },
      count: counts => counts.downstreamDatasources,
      path: (urls, id) => urls.flowLineageDownstreamDatasourcesPath(id),
      paneFactory: (paneParams) => (
        <PaneWithTitle title={paneParams.title}>
          <DatasourcesGridSortProvider>
            {({ orderBy, updateSortOrder }) =>
              <FlowDownstreamDatasourcesQueryGrid
                flowVizportalUrlId={paneParams.flowVizportalUrlId}
                filteredFlowOutputFieldId={paneParams.filteredFlowOutputFieldId}
                rowCount={paneParams.count}
                orderBy={orderBy}
                updateSortOrder={updateSortOrder}
              />}
          </DatasourcesGridSortProvider>
        </PaneWithTitle>
      )
    },
    ...(lensLineageEnabled ? [flowDownstreamLenses] : []),
    {
      lineageItemType: LineageItemType.Workbook,
      messagesForLineage: { withCount: Messages.WorkbooksWithCount, withoutCount: Messages.Workbooks },
      messagesForPane: { withCount: Messages.DownstreamWorkbooksWithCount, withoutCount: Messages.DownstreamWorkbooks },
      count: counts => counts.downstreamWorkbooks,
      path: (urls, id) => urls.flowLineageDownstreamWorkbooksPath(id),
      paneFactory: (paneParams) => (
        <PaneWithTitle title={paneParams.title}>
          <WorkbooksGridSortProvider>
            {({ orderBy, updateSortOrder }) =>
              <FlowDownstreamWorkbooksQueryGrid
                flowVizportalUrlId={paneParams.flowVizportalUrlId}
                filteredFlowOutputFieldId={paneParams.filteredFlowOutputFieldId}
                rowCount={paneParams.count}
                orderBy={orderBy}
                updateSortOrder={updateSortOrder}
              />}
          </WorkbooksGridSortProvider>
        </PaneWithTitle>
      )
    },
    {
      lineageItemType: LineageItemType.Sheet,
      messagesForLineage: { withCount: Messages.SheetsWithCount, withoutCount: Messages.Sheets },
      messagesForPane: { withCount: Messages.DownstreamSheetsWithCount, withoutCount: Messages.DownstreamSheets },
      count: counts => counts.downstreamSheets,
      path: (urls, id) => urls.flowLineageDownstreamSheetsPath(id),
      paneFactory: (paneParams) => (
        <PaneWithTitle title={paneParams.title}>
          <SheetsGridSortProvider>
            {({ orderBy, updateSortOrder }) =>
              <FlowDownstreamSheetsQueryGrid
                flowVizportalUrlId={paneParams.flowVizportalUrlId}
                filteredFlowOutputFieldId={paneParams.filteredFlowOutputFieldId}
                rowCount={paneParams.count}
                orderBy={orderBy}
                updateSortOrder={updateSortOrder}
              />}
          </SheetsGridSortProvider>
        </PaneWithTitle>
      ),
    },
    {
      lineageItemType: LineageItemType.Dashboard,
      messagesForLineage: { withCount: Messages.DashboardsWithCount, withoutCount: Messages.Dashboards },
      messagesForPane: { withCount: Messages.DownstreamDashboardsWithCount, withoutCount: Messages.DownstreamDashboards },
      count: counts => counts.downstreamDashboards,
      path: (urls, id) => urls.flowLineageDownstreamDashboardsPath(id),
      paneFactory: (paneParams) => (
        <PaneWithTitle title={paneParams.title}>
          <DashboardsGridSortProvider>
            {({ orderBy, updateSortOrder }) =>
              <FlowDownstreamDashboardsQueryGrid
                flowVizportalUrlId={paneParams.flowVizportalUrlId}
                filteredFlowOutputFieldId={paneParams.filteredFlowOutputFieldId}
                rowCount={paneParams.count}
                orderBy={orderBy}
                updateSortOrder={updateSortOrder}
              />}
          </DashboardsGridSortProvider>
        </PaneWithTitle>
      ),
    },
    ...(metricsDataCatalogEnabled ? [flowDownstreamMetrics] : []),
  ];
}
const memoizedGetFlowDownstreamItems = defaultMemoize(getFlowDownstreamItems);

const FlowOwners: FlowLineageItemConfig = {
  lineageItemType: LineageItemType.User,
  messagesForLineage: { withCount: Messages.OwnersWithCount, withoutCount: Messages.Owners },
  messagesForPane: { withCount: Messages.DownstreamOwnersWithCount, withoutCount: Messages.DownstreamOwners },
  count: counts => counts.downstreamOwners,
  path: (urls, id) => urls.flowLineageDownstreamOwnersPath(id),
  paneFactory: (paneParams) => (
    <FlowDownstreamOwnersPane
      title={paneParams.title}
      flowVizportalUrlId={paneParams.flowVizportalUrlId}
      filteredFlowOutputFieldId={paneParams.filteredFlowOutputFieldId}
      rowCount={paneParams.count}
      flowLuid={paneParams.flowLuid}
    />
  ),
};

function getFlowLineageConfiguration
  (metricsDataCatalogEnabled: boolean,
    publishedConnectionsLineageEnabled: boolean,
    lensLineageEnabled: boolean,
  ): ReadonlyArray<FlowLineageSection> {
  return [
    {
      sectionType: LineageSectionType.Group,
      key: 'upstream',
      items: memoizedGetFlowUpstreamItems(publishedConnectionsLineageEnabled),
    },
    {
      sectionType: LineageSectionType.Anchor,
      key: 'anchor',
      items: [FlowAnchor()],
    },
    {
      sectionType: LineageSectionType.Group,
      key: 'downstream',
      items: memoizedGetFlowDownstreamItems(metricsDataCatalogEnabled, lensLineageEnabled),
    },
    {
      sectionType: LineageSectionType.Owners,
      key: 'owners',
      items: [FlowOwners],
    },
  ];
}
export const memoizedGetFlowLineageConfiguration = defaultMemoize(getFlowLineageConfiguration);
